import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

/**
 * Journey status vocabulary, kept here because this dialog is the only piece
 * of the orders UI still living in sundae — the rest moved to the standalone
 * ACOT Orders Tracker at the 2026-09-05 cutover, taking features/orders/lib
 * with it. The orders app owns the canonical copy; these three are the slice
 * a picker row needs (chip label, chip tone, date stamp).
 */
const STATUS_LABELS: Record<string, string> = {
  announced: 'To be ordered',
  placed: 'Placed',
  confirmed: 'Confirmed',
  invoiced: 'Invoiced',
  partially_shipped: 'Partially shipped',
  shipped: 'Shipped',
  partially_received: 'Partially received',
  received: 'Received',
  reconciled: 'Reconciled',
  closed: 'Closed',
  cancelled: 'Cancelled',
  wont_order: "Won't order",
};

const STATUS_TONE: Record<string, string> = {
  announced: 'bg-fresh/10 text-fresh-ink',
  placed: 'bg-muted text-muted-foreground',
  confirmed: 'bg-info/10 text-info-ink',
  invoiced: 'bg-warning/10 text-warning-ink',
  partially_shipped: 'bg-info/10 text-info-ink',
  shipped: 'bg-info/10 text-info-ink',
  partially_received: 'bg-accent2/10 text-accent2-ink',
  received: 'bg-accent2/10 text-accent2-ink',
  reconciled: 'bg-success/10 text-success-ink',
  closed: 'bg-muted text-muted-foreground',
  cancelled: 'bg-muted text-muted-foreground line-through',
  wont_order: 'bg-muted text-muted-foreground line-through',
};

/** Date-only strings parse as UTC midnight; pin them to local time so they don't render a day early. */
function fmtDate(value: string | null | undefined): string {
  if (!value) return '';
  try {
    const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
    return format(d, 'MMM d');
  } catch {
    return value;
  }
}

/**
 * What the picker needs to name, rank and stamp a journey — nothing more.
 * Structural, not imported: the rows arrive over the §3d proxy from the
 * standalone orders app (api/orderJourneyLink), and the dialog never has to
 * know where they came from or what picking one does.
 */
export interface JourneyOption {
  id: string;
  vendor: string;
  label?: string | null;
  status: string;
  placedDate?: string | null;
  orderDueDate?: string | null;
  archivedAt?: string | null;
  updatedAt?: string | null;
  receivingIds?: readonly (string | number)[];
}

/**
 * The journey picker: search, sender-matched ranking, one click to attach.
 *
 * Deliberately says nothing about WHERE the journeys come from or what
 * attaching does — both are the caller's. Today the one caller is the mail
 * reader (DetailHeader): it lists journeys from the standalone orders app
 * through sundae's §3d proxy, and picking links the open email to one.
 */
export function AddToOrderJourneyDialog({
  open, onOpenChange, vendorGuess,
  title = 'Add to order journey',
  description = 'Links this email to the journey now (visible in its history) and queues it for the '
    + 'next orders run to read — order lines, invoice detail, status, whatever it fills.',
  successNote = 'The next run will process it.',
  loadJourneys,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sender display name / address — seeds the ranking, not a filter. */
  vendorGuess?: string | null;
  title?: string;
  description?: string;
  successNote?: string;
  /** Where the journeys come from. Called once per open. */
  loadJourneys: () => Promise<JourneyOption[]>;
  /** What picking a journey does. */
  onPick: (journey: JourneyOption) => Promise<void>;
}) {
  const [orders, setOrders] = useState<JourneyOption[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [linkedTo, setLinkedTo] = useState<JourneyOption | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setOrders(null);
    setLoadError(null);
    setSearch('');
    setLinkedTo(null);
    setLinkError(null);
    loadJourneys()
      .then(setOrders)
      .catch((e: Error) => setLoadError(e.message));
    // loadJourneys is a fresh closure on every render of the caller; depending
    // on it would refetch the list under the user mid-search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const rows = useMemo(() => {
    if (!orders) return [];
    const q = search.trim().toLowerCase();
    const guess = (vendorGuess || '').toLowerCase();
    let out = orders.filter((o) => !o.archivedAt);
    if (q) {
      out = out.filter((o) =>
        o.vendor.toLowerCase().includes(q)
        || (o.label || '').toLowerCase().includes(q)
        || (o.receivingIds || []).some((id) => String(id).includes(q)));
    }
    // Sender-matched journeys float; within a band, most recently updated first.
    const score = (o: JourneyOption) => {
      const v = o.vendor.toLowerCase();
      if (!guess) return 0;
      if (guess.includes(v) || v.includes(guess)) return 1;
      return v.split(/\s+/).some((word) => word.length > 3 && guess.includes(word)) ? 0.5 : 0;
    };
    return [...out].sort((a, b) =>
      score(b) - score(a) || (b.updatedAt || '').localeCompare(a.updatedAt || ''));
  }, [orders, search, vendorGuess]);

  const link = async (order: JourneyOption) => {
    setBusyId(order.id);
    setLinkError(null);
    try {
      await onPick(order);
      setLinkedTo(order);
    } catch (e) {
      setLinkError(e instanceof Error ? e.message : 'Failed to link to the journey');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {linkedTo ? (
          <div className="flex items-center gap-2 rounded-md border border-success/40 bg-success/5 px-3 py-2 text-sm text-success-ink">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            Linked to {linkedTo.vendor}{linkedTo.label ? ` — ${linkedTo.label}` : ''}. {successNote}
          </div>
        ) : (
          <>
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vendor, note, or receiving #"
              className="h-8 text-sm"
            />
            {linkError && (
              <div className="text-xs text-danger-ink">{linkError}</div>
            )}
            <div className="h-[45vh] overflow-y-auto rounded-md border border-border/60">
              {loadError && <div className="p-3 text-xs text-destructive">{loadError}</div>}
              {!orders && !loadError && (
                <div className="flex items-center justify-center gap-2 py-8 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading journeys…
                </div>
              )}
              {orders && rows.length === 0 && (
                <div className="py-8 text-center text-xs text-muted-foreground">No matching journeys.</div>
              )}
              {rows.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  disabled={busyId != null}
                  className="flex w-full flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-border/40 px-3 py-2 text-left text-sm hover:bg-muted/40 disabled:opacity-60"
                  onClick={() => link(o)}
                >
                  {busyId === o.id
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    : null}
                  <span className="font-medium">{o.vendor}</span>
                  {o.label && <span className="truncate text-xs text-muted-foreground">{o.label}</span>}
                  <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-medium', STATUS_TONE[o.status] || 'bg-muted text-muted-foreground')}>
                    {STATUS_LABELS[o.status] || o.status}
                  </span>
                  <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">
                    {o.placedDate ? `placed ${fmtDate(o.placedDate)}` : o.orderDueDate ? `due ${fmtDate(o.orderDueDate)}` : ''}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
